import type { OptionMenuItem } from '@getaccept/lib-shared-new/src/options-menu/types/option-menu-item';
import { t } from '@getaccept/lib-shared-new/src/helpers/translation.helper';
import type { DateTime } from 'luxon';
import { DateHelper } from '@getaccept/lib-shared-new/src/helpers/date.helper';
import { FileMenuOption } from '../types/enums/file-menu-option';
import { allAcceptedFileTypes } from '../constants/accepted-file-types';

export class FileHelper {
  static getOptions = (pinned: boolean): OptionMenuItem[] => [
    {
      title: t('files_download_file'),
      icon: 'download',
      callback: FileMenuOption.Download,
    },
    ...(pinned
      ? [
          {
            title: t('files_unpin_file'),
            icon: 'pin',
            class: 'pinned',
            callback: FileMenuOption.UnPin,
          },
        ]
      : [
          {
            title: t('files_pin_file'),
            icon: 'pin',
            callback: FileMenuOption.Pin,
          },
        ]),
    {
      title: t('files_file_analytics'),
      icon: 'bars',
      callback: FileMenuOption.Statistics,
    },
    {
      title: t('files_delete_file'),
      icon: 'delete-bin',
      class: 'delete-option',
      callback: FileMenuOption.Delete,
    },
  ];

  static getPendoDataLabel = (option: FileMenuOption) => {
    switch (option) {
      case FileMenuOption.Download:
        return 'file-options-download-button';
      case FileMenuOption.Pin:
        return 'file-options-pin-button';
      case FileMenuOption.UnPin:
        return 'file-options-unpin-button';
      case FileMenuOption.Statistics:
        return 'file-options-statistics-button';
      case FileMenuOption.Delete:
        return 'file-options-delete-button';
      default:
        return '';
    }
  };

  static downloadFile = (url: string) => {
    const link = window.document.createElement('a');
    link.href = url;
    window.document.body.appendChild(link);
    link.click();
    link.remove();
  };

  static validFileType(fileType: string): boolean {
    if (!fileType) {
      return false;
    }
    return allAcceptedFileTypes.includes(fileType.toLowerCase());
  }

  static getDateTime = (date: DateTime) => {
    const todayPlusMinusOne = DateHelper.todayPlusMinusOne(date);
    if (todayPlusMinusOne) {
      const time = date.toLocaleString({
        hour: 'numeric',
        minute: 'numeric',
      });
      return `${todayPlusMinusOne}, ${time}`;
    }
    if (DateHelper.isSameYear(date)) {
      return date.toLocaleString({
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
      });
    }
    return date.toLocaleString({
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  };

  static formatSize = (size: number) => {
    if (!size) {
      return '-';
    }

    const formattedSize = new Intl.NumberFormat('en', {
      style: 'unit',
      unit: 'byte',
      notation: 'compact',
      unitDisplay: 'narrow',
    })
      .format(size)
      .replace(/([\d.]+)([A-Za-z]+)/, '$1 $2');

    // GB is formatted as BB in Intl.NumberFormat
    if (formattedSize.includes('BB')) {
      return formattedSize.replace('BB', 'GB');
    }

    return formattedSize;
  };
}
